import type { AggField } from '../types'
import type { SupportedTools } from '../../../interfaces'

/**
 * Per-tool, per-category field list and aggregation overrides for DataDisplay.
 *
 * Semantics:
 * - `summary` / `detail` — **additive**: appended to the global defaultDisplayFields for this
 *   category. Use this to add tool-specific fields beyond the cross-tool defaults.
 * - `identityFields` — **additive**: appended to the default identityFields for this category.
 *   Use this to add extra groupBy fields specific to the tool. Must be CCM-mapped fields.
 * - `aggFields` — **replacement**: when set, fully replaces auto-derivation. Fields with
 *   `fromOverride: true` (set automatically at resolve time) bypass `fieldExists` filtering,
 *   so tool-specific field names (not in CCM) pass through via mapField's `?? c` fallback.
 *
 * Example:
 *   'CrowdStrike LogScale': {
 *     process_creation: {
 *       detail: ['vendor_specific_field'],     // appended after defaults
 *       aggFields: [
 *         { field: 'username', fn: 'values' }, // collect fields; aid/timestamp hardcoded in template
 *       ],
 *     },
 *   }
 */
export interface ToolDisplayFields {
  summary?: string[]         // additive: extra summary fields beyond global defaults
  detail?: string[]          // additive: extra detail fields beyond global defaults
  identityFields?: string[]  // additive: extra groupBy fields beyond global defaults (must be CCM-mapped)
  aggFields?: AggField[]     // replacement: explicit agg field list — bypasses auto-derivation when set
}

export type ToolDataDisplayOverrides = Partial<
  Record<SupportedTools, Record<string, ToolDisplayFields>>
>

/**
 * Per-tool, per-category DataDisplay field and aggregation overrides.
 *
 * For LogScale, count(aid), min/max(@timestamp), and formattime post-processing are hardcoded
 * universally in the LogScale groupBy template renderer (crowdstrike-logscale.tool.ts).
 * These overrides only specify the category-specific collect fields.
 *
 * identityFields are omitted where they match defaults — the additive merge produces no change.
 */
export const toolDataDisplayOverrides: ToolDataDisplayOverrides = {
  'CrowdStrike LogScale': {
    process_creation: {
      detail: [
        'sha256',
        'md5',
      ],
    },
    file_event: {
      summary: ['event_type'],
      detail: [
      'event_type',
      'process_name',
      'process_id'
      ],
    },
    network_connection: {
      detail: [
        'event_type',
        'process_name',
        'process_id',
        'protocol',
        'src_port',
      ],
    },
    pipe_created: {
      detail: [
        'event_type',
        'process_path',
        'target_path',
      ],
    },
    dns: {
      detail: [
        'process_name',
        'process_path',
        'process_id',
      ],
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'hostname', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'dns_query', fn: 'values' },
        { field: 'dns_answer', fn: 'values' },
        { field: 'dest_ip', fn: 'values' },
        { field: 'IP4Records', fn: 'values' },
        { field: 'CNAMERecords', fn: 'values' },
        { field: 'RequestType', fn: 'values' },
      ],
    },
    registry_event: {
      summary: ['event_type'],
      detail: [
        'event_type',
        'process_id',
        'process_name',
        'process_path',
      ],    
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'username', fn: 'values' },
        { field: 'registry_path', fn: 'values' },
        { field: 'registry_key_name', fn: 'values' },
        { field: 'registry_value_name', fn: 'values' },
        { field: 'RegBinaryValue', fn: 'values' },
        { field: 'RegNumericValue', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'TargetFileName', fn: 'values' },
        { field: 'TargetCommandLineParameters', fn: 'values' },
        { field: 'registryKey_Object_Data', fn: 'values' },
      ],
    },
    image_load: {
      detail: [
        'event_type',
      ],
    },
  },
  'Microsoft Defender': {
    process_creation: {
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'username', fn: 'values' },
        { field: 'AccountDomain', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'parent_process_name', fn: 'values' },
        { field: 'parent_process_cmdline', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'parent_process_id', fn: 'values' },
        { field: 'InitiatingProcessParentFileName', fn: 'values' },
        { field: 'InitiatingProcessParentId', fn: 'values' },
        { field: 'InitiatingProcessAccountDomain', fn: 'values' },
        { field: 'InitiatingProcessAccountName', fn: 'values' },
        { field: 'ProcessVersionInfoCompanyName', fn: 'values' },
        { field: 'ProcessVersionInfoProductName', fn: 'values' },
        { field: 'ProcessVersionInfoProductVersion', fn: 'values' },
        { field: 'ProcessVersionInfoInternalFileName', fn: 'values' },
        { field: 'ProcessVersionInfoOriginalFileName', fn: 'values' },
        { field: 'ProcessVersionInfoFileDescription', fn: 'values' },
        { field: 'FileSize', fn: 'values' },
        { field: 'md5', fn: 'values' },
        { field: 'sha256', fn: 'values' },
        { field: 'DeviceId', fn: 'values' },
        { field: 'ReportId', fn: 'values' },
      ],
    },
    registry_event: {
      aggFields: [
        { field: 'InitiatingProcessAccountName', fn: 'values' },
        { field: 'InitiatingProcessParentFileName', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'event_type', fn: 'values' },
        { field: 'registry_path', fn: 'values' },
        { field: 'registry_key_name', fn: 'values' },
        { field: 'registry_value_name', fn: 'values' },
        { field: 'PreviousRegistryValueName', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'InitiatingProcessParentId', fn: 'values' },
        { field: 'DeviceId', fn: 'values' },
        { field: 'ReportId', fn: 'values' },
      ],
    },
    file_event: {
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'FileName', fn: 'values' },
        { field: 'PreviousFolderPath', fn: 'values' },
        { field: 'PreviousFileName', fn: 'values' },
        { field: 'FileSize', fn: 'values' },
        { field: 'md5', fn: 'values' },
        { field: 'sha256', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'InitiatingProcessSHA256', fn: 'values' },
        { field: 'InitiatingProcessParentFileName', fn: 'values' },
        { field: 'InitiatingProcessParentId', fn: 'values' },
        { field: 'InitiatingProcessAccountDomain', fn: 'values' },
        { field: 'InitiatingProcessAccountName', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoCompanyName', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoProductName', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoProductVersion', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoInternalFileName', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoOriginalFileName', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoFileDescription', fn: 'values' },
        { field: 'DeviceId', fn: 'values' },
        { field: 'ReportId', fn: 'values' },
      ],
    },
    image_load: {
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'FileName', fn: 'values' },
        { field: 'target_path', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'InitiatingProcessParentFileName', fn: 'values' },
        { field: 'InitiatingProcessParentId', fn: 'values' },
        { field: 'InitiatingProcessAccountDomain', fn: 'values' },
        { field: 'InitiatingProcessAccountName', fn: 'values' },
        { field: 'FileSize', fn: 'values' },
        { field: 'md5', fn: 'values' },
        { field: 'sha256', fn: 'values' },
        { field: 'ReportId', fn: 'values' },
      ],
    },
    pipe_created: {
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'pipe_name', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'InitiatingProcessParentFileName', fn: 'values' },
        { field: 'InitiatingProcessParentId', fn: 'values' },
        { field: 'AdditionalFields', fn: 'values' },
        { field: 'InitiatingProcessVersionInfoOriginalFileName', fn: 'values' },
        { field: 'InitiatingProcessAccountSid', fn: 'values' },
        { field: 'InitiatingProcessAccountName', fn: 'values' },
        { field: 'InitiatingProcessAccountDomain', fn: 'values' },
        { field: 'ReportId', fn: 'values' },
        { field: 'DeviceId', fn: 'values' },
      ],
    },
    network_connection: {
      aggFields: [
        { field: 'event_type', fn: 'values' },
        { field: 'url', fn: 'values' },
        { field: 'src_ip', fn: 'values' },
        { field: 'dest_ip', fn: 'values' },
        { field: 'dest_port', fn: 'values' },
        { field: 'protocol', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'InitiatingProcessAccountName', fn: 'values' },
        { field: 'DeviceId', fn: 'values' },
      ],
    },
  },
  'Microsoft Sentinel': {
    process_creation: {
      identityFields: ['SourceComputerId'],
      aggFields: [
        { field: 'event_id', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'parent_process_id', fn: 'values' },
        { field: 'message', fn: 'values' },
      ],
    },
    registry_event: {
      identityFields: ['SourceComputerId'],
      aggFields: [
        { field: 'event_id', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'registry_path', fn: 'values' },
        { field: 'registry_key_name', fn: 'values' },
        { field: 'registry_new_value_name', fn: 'values' },
        { field: 'registry_old_value_name', fn: 'values' },
      ],
    },
    file_event: {
      identityFields: ['SourceComputerId'],
      aggFields: [
        { field: 'event_id', fn: 'values' },
        { field: 'object_name', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'username', fn: 'values' },
      ],
    }
  },
  'SentinelOne - PowerQuery': {
    process_creation: {
      detail: [
        'sha256',
        'md5',
      ],
    },
    file_event: {
      summary: ['event_type'],
      detail: [
      'event_type',
      'process_name',
      'process_id'
      ],
    },
    network_connection: {
      detail: [
        'event_type',
        'process_name',
        'process_id',
        'protocol',
        'src_port',
      ],
    },
    pipe_created: {
      detail: [
        'event_type',
        'process_path',
        'target_path',
      ],
    },
    dns: {
      detail: [
        'process_name',
        'process_path',
        'process_id',
      ],
    },
    registry_event: {
      summary: ['event_type'],
      detail: [
        'event_type',
        'process_id',
        'process_name',
        'process_path',
      ],    
    },
    image_load: {
      detail: [
        'event_type',
      ],
    },
  },
  'Palo Alto Cortex XDR': {
    process_creation: {
      aggFields: [
        // CCM-mapped fields
        { field: 'hostname', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'parent_process_cmdline', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'parent_process_id', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'username', fn: 'values' },
        { field: 'sha256', fn: 'values' },
        // Non-CCM tool-specific fields (pass through via fromOverride)
        { field: 'event_type', fn: 'values' },
        { field: 'event_sub_type', fn: 'values' },
        { field: 'causality_actor_process_image_path', fn: 'values' },
        { field: 'causality_actor_process_os_pid', fn: 'values' },
        { field: 'action_process_requested_parent_pid', fn: 'values' },
        { field: 'actor_causality_id', fn: 'values' },
        { field: 'action_process_signature_product', fn: 'values' },
        { field: 'action_process_signature_vendor', fn: 'values' },
        { field: 'action_process_signature_status', fn: 'values' },
        { field: 'action_process_integrity_level', fn: 'values' },
        { field: 'os_actor_process_file_original_name', fn: 'values' },
        { field: 'os_actor_process_file_internal_name', fn: 'values' },
      ],
    },
    file_event: {
      aggFields: [
        // CCM-mapped fields
        { field: 'hostname', fn: 'values' },
        { field: 'target_path', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'username', fn: 'values' },
        // Non-CCM tool-specific fields
        { field: 'event_type', fn: 'values' },
        { field: 'event_sub_type', fn: 'values' },
        { field: 'causality_actor_process_image_path', fn: 'values' },
        { field: 'causality_actor_process_os_pid', fn: 'values' },
        { field: 'actor_causality_id', fn: 'values' },
        { field: 'actor_process_image_sha256', fn: 'values' },
        { field: 'actor_process_signature_product', fn: 'values' },
        { field: 'actor_process_signature_vendor', fn: 'values' },
        { field: 'actor_process_signature_status', fn: 'values' },
        { field: 'actor_process_integrity_level', fn: 'values' },
        { field: 'actor_process_file_original_name', fn: 'values' },
        { field: 'actor_process_file_internal_name', fn: 'values' },
      ],
    },
    registry_event: {
      aggFields: [
        // CCM-mapped fields
        { field: 'process_cmdline', fn: 'values' },
        { field: 'registry_path', fn: 'values' },
        { field: 'registry_key_name', fn: 'values' },
        { field: 'registry_value_name', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'username', fn: 'values' },
        { field: 'process_cmdline', fn: 'dcount' },
        // Non-CCM tool-specific fields
        { field: 'causality_actor_process_image_path', fn: 'values' },
        { field: 'causality_actor_process_os_pid', fn: 'values' },
      ],
    },
    image_load: {
      aggFields: [
        // CCM-mapped fields
        { field: 'hostname', fn: 'values' },
        { field: 'object_loaded', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'username', fn: 'values' },
        // Non-CCM tool-specific fields
        { field: 'event_type', fn: 'values' },
        { field: 'event_sub_type', fn: 'values' },
        { field: 'causality_actor_process_image_path', fn: 'values' },
        { field: 'causality_actor_process_os_pid', fn: 'values' },
        { field: 'actor_causality_id', fn: 'values' },
        { field: 'actor_process_image_sha256', fn: 'values' },
        { field: 'actor_process_signature_product', fn: 'values' },
        { field: 'actor_process_signature_vendor', fn: 'values' },
        { field: 'actor_process_signature_status', fn: 'values' },
        { field: 'actor_process_integrity_level', fn: 'values' },
        { field: 'actor_process_file_original_name', fn: 'values' },
        { field: 'actor_process_file_internal_name', fn: 'values' },
      ],
    },
    network_connection: {
      aggFields: [
        // CCM-mapped fields
        { field: 'hostname', fn: 'values' },
        { field: 'src_ip', fn: 'values' },
        { field: 'src_port', fn: 'values' },
        { field: 'dest_ip', fn: 'values' },
        { field: 'dest_port', fn: 'values' },
        { field: 'process_name', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'username', fn: 'values' },
        // Non-CCM tool-specific fields
        { field: 'causality_actor_process_image_name', fn: 'values' },
        { field: 'causality_actor_process_command_line', fn: 'values' },
        { field: 'causality_actor_process_image_path', fn: 'values' },
        { field: 'causality_actor_process_os_pid', fn: 'values' },
      ],
    },
    dns: {
      aggFields: [
        // CCM-mapped fields
        { field: 'src_ip', fn: 'values' },
        { field: 'dest_ip', fn: 'values' },
        { field: 'dns_query', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'username', fn: 'values' },
        // Non-CCM tool-specific fields
        { field: 'dns_query_type', fn: 'values' },
        { field: 'dns_reply_code', fn: 'values' },
        { field: 'causality_actor_process_image_path', fn: 'values' },
        { field: 'actor_process_signature_product', fn: 'values' },
        { field: 'actor_process_signature_vendor', fn: 'values' },
        { field: 'actor_process_signature_status', fn: 'values' },
      ],
    },
  },
  'Google SecOps': {
    process_creation: {
      aggFields: [
        { field: 'event_id', fn: 'values' },
        { field: 'parent_process_path', fn: 'values' },
        { field: 'parent_process_id', fn: 'values' },
        { field: 'process_path', fn: 'values' },
        { field: 'process_id', fn: 'values' },
        { field: 'process_cmdline', fn: 'values' },
        { field: 'process_cmdline', fn: 'count' },
        { field: 'process_cmdline', fn: 'dcount' },
      ],
    },
    file_event: {
      // identityFields: defaults to ['hostname', 'target_path'] from global
      // target_path stays in match (groupBy) — results are per-host, per-file
      aggFields: [
        { field: 'event_id', fn: 'values' },
        { field: 'process_path', fn: 'values' },   // → parent_process_path via category override
        { field: 'process_id', fn: 'values' },     // → parent_process_id via category override
        { field: 'process_cmdline', fn: 'values' },
        { field: 'event_id', fn: 'count' },        // total event count
      ],
    },
    dns: {
      aggFields: [
        { field: 'event_id', fn: 'values' },
        { field: 'dns_query', fn: 'values' },
        { field: 'process_path', fn: 'values' },   // → parent_process_path via category override
        { field: 'process_id', fn: 'values' },     // → parent_process_id via category override
        { field: 'dns_query', fn: 'dcount' },
      ],
    },
  },
}
